import mixpanel from "mixpanel-browser";
import {
  getAnalytics,
  logEvent,
  setUserId,
  setUserProperties,
} from "firebase/analytics";
import { AnalyticsEvent } from "./model";
import { firebaseApp } from "../firebase/client-app";
import { DbUser } from "../user/models";
import { config } from "../config";

class AnalyticsService {
  private mixpanel: typeof mixpanel | null = null;
  private firebaseAnalytics: ReturnType<typeof getAnalytics> | null = null;
  private pendingEvents: Array<[string, Record<string, any>?]> = [];

  constructor() {
    this.init();
  }

  private init() {
    // Check if the window object is available (i.e., we're in a browser environment)
    if (typeof window !== "undefined") {
      mixpanel.init(config.MIXPANEL_TOKEN);
      this.mixpanel = mixpanel;
      this.firebaseAnalytics = getAnalytics(firebaseApp);

      while (this.pendingEvents.length > 0) {
        const [event, properties] = this.pendingEvents.shift()!;
        this._track(event, properties, true);
      }
    }
  }

  public track(event: AnalyticsEvent, properties?: Record<string, any>) {
    this._track(event, properties);
  }

  public trackCustom(event: string, properties?: Record<string, any>) {
    this._track(event, properties);
  }

  public trackError(event: AnalyticsEvent, error: Error) {
    this._track(event, { error: error.message });
  }

  private _track(
    event: string,
    properties?: Record<string, any>,
    isPending = false,
  ) {
    if (!isPending && (!this.mixpanel || !this.firebaseAnalytics)) {
      this.pendingEvents.push([event, properties]);
      return;
    }

    try {
      this.mixpanel?.track(event, properties);
      logEvent(this.firebaseAnalytics!, event, properties);
    } catch (error) {
      console.error(error);
    }
  }

  public setUser(user: DbUser) {
    if (!this.mixpanel || !this.firebaseAnalytics) return;

    const userProperties = {
      user_id: user.id,
      user_name: user.user_name,
      user_email: user?.email,
      user_phone: user.phone,
      created_at: user?.created_at,
    };

    this.mixpanel?.identify(user.id);
    this.mixpanel?.register(userProperties);

    setUserId(this.firebaseAnalytics!, user.id);
    setUserProperties(this.firebaseAnalytics!, userProperties);
  }

  public clearUser() {
    if (!this.mixpanel || !this.firebaseAnalytics) return;

    this.mixpanel?.reset();

    setUserId(this.firebaseAnalytics!, null);
    setUserProperties(this.firebaseAnalytics!, {});
  }
}

export const analyticsService = new AnalyticsService();
